import { Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators } from "@angular/forms";

@Component({
  selector: "app-love-it-input",
  templateUrl: "./love-it-input.component.html",
  styleUrls: ["./love-it-input.component.scss"],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LoveItInputComponent),
      multi: true,
    },
  ],
})
export class LoveItInputComponent implements ControlValueAccessor, OnChanges {
  @Input() formControl: FormControl;
  @Input() submitted: boolean;
  @Input() label: string;
  @Input() suffix: string;
  @Input() type: string;
  @Input() placeholder: string;
  @Input() min: string;
  @Input() max: string;
  @Input() decimalLimit: number = -1;
  @Input() disabled: boolean;
  @Input() value: string = "";

  @Output() onTextEdited: EventEmitter<string> = new EventEmitter<string>();
  @Output() ionBlur: EventEmitter<string> = new EventEmitter<string>();
  @Output() ionFocus: EventEmitter<string> = new EventEmitter<string>();

  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges) {
    if (changes["disabled"] && this.formControl) {
      if (this.disabled) {
        this.formControl.disable({ emitEvent: false });
      } else {
        this.formControl.enable({ emitEvent: false });
      }
    }
  }

  constructor() {}

  writeValue(value: string): void {
    this.value = value ?? "";
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onIonBlur(event) {
    const value = event?.detail?.value ?? this.formControl.value ?? "";
    this.ionBlur.emit(value);
    this.limitDecimals(event);
    this.parseDate(event, true);
    this.onTouched();
  }

  onIonFocus(event) {
    const value = event?.detail?.value ?? this.formControl.value ?? "";
    this.ionFocus.emit(value);
    this.limitDecimals(event);
  }

  onInputChange(event) {
    const value = event?.detail?.value ?? this.formControl.value ?? "";
    this.onTextEdited.emit(value);
    this.limitDecimals(event);
    this.parseDate(event);
    this.onChange(this.formControl.value ?? "");
  }

  onIonInput(event) {
    this.limitDecimals(event);
    this.parseDate(event);
    this.onChange(this.formControl.value ?? "");
  }

  limitDecimals(event) {
    if (this.decimalLimit >= 0) {
      let value = event.target.value;

      value = value.replace(/[^0-9.]/g, "");

      const parts = value.split(".");

      if (parts.length > 2) {
        value = parts[0] + "." + parts[1];
      }

      if (this.decimalLimit === 0) {
        value = parts[0];
      } else if (parts[1]?.length > this.decimalLimit) {
        parts[1] = parts[1].substring(0, this.decimalLimit);
        value = parts.join(".");
      }

      this.formControl.setValue(value, { emitEvent: false });
    }
  }

  parseDate(event, handleSingleDigitYear: boolean = false) {
    if (this.type == "date") {
      let datestr = event.target.value;

      const handleDateMatch = (match: RegExpMatchArray) => {
        let [, yearGroup, monthGroup, dayGroup] = match;
        const dateOutputString = (year: string, month: string, day: string): string => {
          return `${year}-${month}-${day}`;
        };
        if (yearGroup.startsWith("000") && !handleSingleDigitYear) {
          return dateOutputString(yearGroup, monthGroup, dayGroup);
        }
        yearGroup = yearGroup.replace(/\D/g, "").slice(-2);
        if (yearGroup.length === 2) {
          if (+yearGroup < (new Date().getFullYear() % 100) + 10) {
            yearGroup = "20" + yearGroup;
          } else {
            yearGroup = "19" + yearGroup;
          }
        }
        return dateOutputString(yearGroup, monthGroup, dayGroup);
      };

      const regexDots = /(\d{2,4})-(\d{1,2})-(\d{1,2})/;
      const matchRegexDots = regexDots[Symbol.match](datestr);
      if (matchRegexDots) {
        datestr = handleDateMatch(matchRegexDots);
        this.formControl.setValue(datestr, { emitEvent: false });
      } else {
        const regexTwoDigitNumbers = /(\d{2,4})-?([0-3]?[0-9])-?([0-1]?[0-9])/;
        const matchTwoDigitNumbers = regexTwoDigitNumbers[Symbol.match](datestr);
        if (matchTwoDigitNumbers) {
          datestr = handleDateMatch(matchTwoDigitNumbers);
          this.formControl.setValue(datestr, { emitEvent: false });
        }
      }
    }
  }

  protected readonly Validators = Validators;
}
